// Blackjack game engine — pure functions, no React.
// Supports: hit, stand, double, surrender, split (including re-split, split aces, DAS).

export const SUITS = ['♠', '♥', '♦', '♣'];
export const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

export const DEFAULT_RULES = {
  decks: 6,
  dealerHitsSoft17: false,    // S17 default
  blackjackPays: 1.5,          // 3:2
  doubleAfterSplit: true,
  surrender: 'late',           // 'late' | 'none'
  penetration: 0.75,           // reshuffle when this fraction has been dealt
  maxSplitHands: 4,
  insuranceAllowed: true,
};

export function hiLoValue(rank) {
  if (['2', '3', '4', '5', '6'].includes(rank)) return 1;
  if (['10', 'J', 'Q', 'K', 'A'].includes(rank)) return -1;
  return 0;
}

export function buildShoe(decks) {
  const shoe = [];
  for (let d = 0; d < decks; d++) {
    for (const s of SUITS) for (const r of RANKS) shoe.push({ rank: r, suit: s });
  }
  for (let i = shoe.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shoe[i], shoe[j]] = [shoe[j], shoe[i]];
  }
  return shoe;
}

export function cardValue(rank) {
  if (rank === 'A') return 11;
  if (['J', 'Q', 'K'].includes(rank)) return 10;
  return parseInt(rank, 10);
}

// 10/J/Q/K all rank as "10" for pair-splitting purposes.
export function pairKey(rank) {
  if (['10', 'J', 'Q', 'K'].includes(rank)) return '10';
  return rank;
}

export function isPair(cards) {
  return cards.length === 2 && pairKey(cards[0].rank) === pairKey(cards[1].rank);
}

export function handTotal(cards) {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    total += cardValue(c.rank);
    if (c.rank === 'A') aces += 1;
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }
  const soft = aces > 0 && total <= 21;
  return { total, soft };
}

export function isBlackjack(cards) {
  return cards.length === 2 && handTotal(cards).total === 21;
}

export function isBust(cards) {
  return handTotal(cards).total > 21;
}

// Settle one finished hand vs dealer. Returns net P/L in units of bet.
// Doesn't handle naturals — those are settled at deal time.
export function settle(playerCards, dealerCards, bet) {
  const p = handTotal(playerCards).total;
  const d = handTotal(dealerCards).total;
  if (p > 21) return -bet;
  if (d > 21) return bet;
  if (p > d) return bet;
  if (p < d) return -bet;
  return 0;
}

// Dealer plays out their hand per rules.
// Returns { dealer, shoe, drawn } — drawn lets the caller update count tracking.
export function dealerPlay(dealerCards, shoe, rules = DEFAULT_RULES) {
  const cards = [...dealerCards];
  let s = [...shoe];
  const drawn = [];
  while (s.length > 0) {
    const { total, soft } = handTotal(cards);
    if (total > 21) break;
    if (total > 17) break;
    if (total === 17 && (!soft || !rules.dealerHitsSoft17)) break;
    const card = s[s.length - 1];
    s = s.slice(0, -1);
    cards.push(card);
    drawn.push(card);
  }
  return { dealer: cards, shoe: s, drawn };
}

export function cardLabel(card) {
  return `${card.rank}${card.suit}`;
}

export function isRedSuit(card) {
  return card.suit === '♥' || card.suit === '♦';
}

// Take N cards off the top of a shoe; returns { cards, shoe }.
export function drawN(shoe, n) {
  const cards = [];
  let s = shoe;
  for (let i = 0; i < n; i++) {
    cards.push(s[s.length - 1]);
    s = s.slice(0, -1);
  }
  return { cards, shoe: s };
}

// Hi-Lo running count delta for a list of cards.
export function countDelta(cards) {
  return cards.reduce((acc, c) => acc + hiLoValue(c.rank), 0);
}
